import { LocaleCode, LocaleCodeToDateFormatMap, formatDateTimeByLocale } from '@doss/common-all'
import { GridCellCoordinates, GridColType } from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import minMax from 'dayjs/plugin/minMax.js'
import utc from 'dayjs/plugin/utc.js'
import { useEffect, useState } from 'react'
import { PiFunction } from 'react-icons/pi'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { Option } from '~/@/components/inputs/selection/utils'
import { TranslationKeys } from '~/common/generated/i18n.generated'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { CoreMetricType, formatBasicNumericMetric } from './aggregation-functions'
import { MUIDataGridCellValueTypes } from './DossTableFooter'

dayjs.extend(utc)
dayjs.extend(minMax)

interface CellMetrics {
  count: number
  sum?: number
  avg?: number
  min?: number | Date
  max?: number | Date
  dateRange?: number // in days
}

interface GridCountProps {
  selectedCellsArray: GridCellCoordinates[]
  columnType?: GridColType
  cellValues: MUIDataGridCellValueTypes[] // The actual values from selected cells
  totalRowCount: number
}

/** Cell selection metric type extends CoreMetricType with 'range' for date columns */
type CellSelectionMetricType = CoreMetricType | 'range'

const getDefaultMetricTypeByColumnType = (columnType?: GridColType): CellSelectionMetricType => {
  switch (columnType) {
    case 'number':
      return 'sum'
    default:
      return 'count'
  }
}

/**
 * Component to display cell selection and grid metrics, selectable metrics based on column type.
 */
export function TableFooterGridMetrics({
  selectedCellsArray,
  columnType,
  cellValues,
  totalRowCount,
}: GridCountProps) {
  const { t } = useTypedTranslation()
  const { locale } = useGlobalContext()
  // User selected metric. Nullable so that unless the user overrides, we can change dynamically based on column type default
  const [selectedMetric, setSelectedMetric] = useState<CellSelectionMetricType | null>(null)

  // Calculate metrics based on selected cells
  const metrics = calculateMetrics(cellValues, columnType)

  // Ensure current metric is valid when the columnType changes.
  // If it is not, switch to the default for the new columnType.
  useEffect(() => {
    if (!columnType || !selectedMetric) {
      return
    }

    // Get valid metrics for current column type
    const validMetrics = getMetricOptions(columnType, t).map((option) => option.value)

    // If current metric isn't valid for this column type, reset to count
    if (!validMetrics.includes(selectedMetric)) {
      setSelectedMetric(getDefaultMetricTypeByColumnType(columnType))
    }
  }, [columnType, selectedMetric, t])

  // If a user selected a metric, use that, otherwise show the default.
  const metric = selectedMetric ?? getDefaultMetricTypeByColumnType(columnType)

  // If no cells are selected or only one cell, or if there are no valid cell values, show total record count
  // For example, when cells in a child table are selected, the parent table footer will have a non-null selectedCellsArray but no valid cell values to calculate metrics
  if (selectedCellsArray.length <= 1 || cellValues.length === 0) {
    return (
      <div className="px-2 text-sm text-gray-9">{t('table.records', { count: totalRowCount })}</div>
    )
  }

  // For multiple selected cells show metrics
  return (
    <div>
      {columnType && isMetricsColumn(columnType) && metrics.count != 0 ? (
        <div className="mx-2 flex items-center justify-end gap-2 rounded-sm border">
          <Dropdown
            className="border-0 text-gray-9"
            onChange={(value) => value && setSelectedMetric(value as CellSelectionMetricType)}
            options={getMetricOptions(columnType, t)}
            startAdornment={<PiFunction className="size-4 text-gray-8" />}
            value={metric}
          />
          <div className="min-w-[100px] items-center justify-end px-2 text-sm text-gray-9">
            {formatCellSelectionMetricValue(metrics, metric, columnType, locale)}
          </div>
        </div>
      ) : (
        <div className="items-center justify-end text-sm text-gray-9">
          {t('table.cells_selected', { count: metrics.count })}
        </div>
      )}
    </div>
  )
}

/**
 * Calculate metrics based on selected cell values and column type.
 */
function calculateMetrics(values: MUIDataGridCellValueTypes[], columnType?: string): CellMetrics {
  const metrics: CellMetrics = {
    count: values.length,
  }

  if (!columnType || values.length === 0) {
    return metrics
  }

  const validValues = values.filter((v) => v !== null && v !== undefined)

  if (columnType === 'number' && validValues.length > 0) {
    const numbers = validValues.map((v) => Number(v))
    metrics.sum = numbers.reduce((a, b) => a + b, 0)
    metrics.avg = metrics.sum / numbers.length
    metrics.min = Math.min(...numbers)
    metrics.max = Math.max(...numbers)
  }

  if ((columnType === 'date' || columnType === 'dateTime') && validValues.length > 0) {
    const dayjsValidValues = validValues.filter((v) => typeof v !== 'boolean') as Exclude<
      MUIDataGridCellValueTypes,
      boolean
    >[]
    const dates = dayjsValidValues.map((v) => dayjs(v))
    const minDate = dayjs.min(dates)
    const maxDate = dayjs.max(dates)

    if (minDate && maxDate) {
      metrics.min = minDate.toDate()
      metrics.max = maxDate.toDate()
      metrics.dateRange = maxDate.diff(minDate, 'day')
    }
  }

  return metrics
}

/**
 * Get metric options based on column type, default to count if not a metrics column.
 */
function getMetricOptions(
  columnType: string,
  t: (key: TranslationKeys) => string,
): Option<CellSelectionMetricType>[] {
  switch (columnType) {
    case 'number':
      return [
        { value: 'sum', label: t('table.sum') },
        { value: 'count', label: t('table.count') },
        { value: 'avg', label: t('table.average') },
        { value: 'min', label: t('table.min') },
        { value: 'max', label: t('table.max') },
      ]
    case 'date':
    case 'dateTime':
      return [
        { value: 'count', label: t('table.count') },
        { value: 'range', label: t('table.range') },
        { value: 'min', label: t('table.earliest') },
        { value: 'max', label: t('table.latest') },
      ]
    default:
      return [{ value: 'count', label: t('table.count') }]
  }
}

/**
 * Check if column type is a metrics column.
 */
function isMetricsColumn(columnType: string): boolean {
  return ['number', 'date', 'dateTime'].includes(columnType)
}

/**
 * Format metric value based on metric type.
 * Uses shared formatBasicNumericMetric for number columns.
 * DayJS conversion for date metrics back into UTC to prevent timezone offset.
 */
function formatCellSelectionMetricValue(
  metrics: CellMetrics,
  metricType: CellSelectionMetricType,
  columnType: string,
  localeCode = LocaleCode.EN_US,
): string {
  // Handle date-specific formatting
  if (columnType === 'date') {
    const dateFormatByLocale = LocaleCodeToDateFormatMap[localeCode]
    switch (metricType) {
      case 'count':
        return `${metrics.count}`
      case 'min':
        if (metrics.min instanceof Date) {
          return dayjs(metrics.min).utc().format(dateFormatByLocale)
        }
        return '-'
      case 'max':
        if (metrics.max instanceof Date) {
          return dayjs(metrics.max).utc().format(dateFormatByLocale)
        }
        return '-'
      case 'range':
        return `${metrics.dateRange ?? 0} days`
    }
  }

  // Handle dateTime-specific formatting
  if (columnType === 'dateTime') {
    switch (metricType) {
      case 'count':
        return `${metrics.count}`
      case 'min':
        if (metrics.min instanceof Date) {
          return formatDateTimeByLocale(metrics.min.toISOString(), localeCode)
        }
        return '-'
      case 'max':
        if (metrics.max instanceof Date) {
          return formatDateTimeByLocale(metrics.max.toISOString(), localeCode)
        }
        return '-'
      case 'range':
        return `${metrics.dateRange ?? 0} days`
    }
  }

  // Handle number columns using shared formatting
  switch (metricType) {
    case 'count':
      return formatBasicNumericMetric(metrics.count, 'count', localeCode)
    case 'sum':
      return formatBasicNumericMetric(metrics.sum ?? null, 'sum', localeCode)
    case 'avg':
      return formatBasicNumericMetric(metrics.avg ?? null, 'avg', localeCode)
    case 'min':
      return formatBasicNumericMetric(
        (metrics.min as number | undefined) ?? null,
        'min',
        localeCode,
      )
    case 'max':
      return formatBasicNumericMetric(
        (metrics.max as number | undefined) ?? null,
        'max',
        localeCode,
      )
    default:
      return ''
  }
}
